import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import CopyAllIcon from '@mui/icons-material/CopyAll';
import {
    Paper,
    Grid,
    TextField,
    Switch,
    Badge,
    Box,
    Typography,
    Tooltip,
    IconButton,
    CircularProgress,
} from '@mui/material';
import { SCUIContent } from '../../../atomic/organisms';
import { SCUICustomDataGrid } from '../../../atomic/molecules';
import { SCUIButton } from '../../../atomic/atoms';
import { PageUrl } from '../../../types/enums';
import { MenuQuery } from '../../../services/query';
import { OperatorQuery } from '../../../services/query';
import { OperatorCommand } from '../../../services/command';
import './AuthorizationManage.scss';
import CheckIcon from '@mui/icons-material/Check';
import { AM } from './authorizationManage.classes';

export const AuthorizationManage: FC<any> = (props: any) => {
    const [searchedAuthorityName, setSearchedAuthorityName] = useState('');
    const [authorityList, setAuthorityList] = useState<any[]>([]);
    const [allMenuEndPoints, setAllMenuEndPoints] = useState<any[]>([]);
    const [copiedRoute, setCopiedRoute] = useState('');
    const [pendingRoutes, setPendingRoutes] = useState<Record<string, boolean>>({});
    const [optimisticSwitches, setOptimisticSwitches] = useState<Record<string, boolean>>({});

    const { t } = useTranslation();

    const query = new URLSearchParams(useLocation().search);
    const idfromUrl = query.get('RoleId');
    const id = props.location.state?.roleId ? props.location.state?.roleId : idfromUrl;
    const roleName = props.location.state?.roleName;

    const showSuccessToast = useCallback((title: string) => {
        Swal.fire({
            title,
            text: '',
            icon: 'success',
            position: 'top-end',
            showConfirmButton: false,
            toast: true,
            iconColor: '#fff',
            timer: 1500,
            customClass: {
                container: 'swal-toast-container',
            },
            background: '#5cb85c',
        });
    }, []);

    const authorityMap = useMemo(() => {
        return new Map(authorityList.map((auth: any) => [auth.authorizedRoute, auth]));
    }, [authorityList]);

    const getRoleAuthorizationId = useCallback((auth: any) => {
        return auth?.roleAuthorizationId ?? auth?.id ?? auth?.roleAuthId ?? auth?.authorizationId;
    }, []);

    const GetRoleAuthorizationList = useCallback(async () => {
        try {
            const res: any = await OperatorQuery.GetRoleAuthorizationList(id);
            if (res?.data) {
                setAuthorityList(res.data);
                return res.data;
            }
        } catch (error) {
            console.log('Error :>> ', error);
        }
        return [];
    }, [id]);

    const GetMenuAllEndPoints = useCallback(async () => {
        try {
            const endpoints: any = await MenuQuery.GetMenuAllEndPoints();
            if (endpoints) {
                setAllMenuEndPoints(endpoints);
            }
        } catch (error) {
            console.log('Error :>> ', error);
        }
    }, []);

    const handleSwitchControl = useCallback(
        async (checked: boolean, row: any) => {
            const route = row.authorizedRoute;
            setPendingRoutes((prev) => ({ ...prev, [route]: true }));
            setOptimisticSwitches((prev) => ({ ...prev, [route]: checked }));

            try {
                if (checked) {
                    const param = {
                        request: {
                            roleId: Number(id),
                            privilegeBinary: row.privilege,
                            authorizedRoute: route,
                        },
                    };
                    await OperatorCommand.PostRoleAuthorizationAdd(param);
                    showSuccessToast(t('general.added'));
                } else {
                    let roleAuthorizationId = getRoleAuthorizationId(authorityMap.get(route));

                    // Fallback for stale local map: refresh once and retry ID resolution.
                    if (!roleAuthorizationId) {
                        const refreshedAuthorityList = await GetRoleAuthorizationList();
                        const refreshedAuth = Array.isArray(refreshedAuthorityList)
                            ? refreshedAuthorityList.find((auth: any) => auth.authorizedRoute === route)
                            : null;
                        roleAuthorizationId = getRoleAuthorizationId(refreshedAuth);
                    }

                    if (!roleAuthorizationId) {
                        throw new Error('Role authorization ID not found');
                    }

                    await OperatorCommand.DeleteRoleAuthorization(roleAuthorizationId);
                    showSuccessToast(t('general.deleted'));
                }

                await GetRoleAuthorizationList();
                setOptimisticSwitches((prev) => {
                    const next = { ...prev };
                    delete next[route];
                    return next;
                });
            } catch (error) {
                setOptimisticSwitches((prev) => {
                    const next = { ...prev };
                    delete next[route];
                    return next;
                });
                Swal.fire('', checked ? t('general.couldntAdded') : t('general.couldntDeleted'), 'error');
                console.log('Error :>> ', error);
            } finally {
                setPendingRoutes((prev) => {
                    const next = { ...prev };
                    delete next[route];
                    return next;
                });
            }
        },
        [authorityMap, getRoleAuthorizationId, GetRoleAuthorizationList, id, showSuccessToast, t]
    );

    const rows = useMemo(() => {
        const searchLower = searchedAuthorityName.trim().toLowerCase();
        const mappedRows = allMenuEndPoints.map((endpoint: any, index: number) => {
            const auth = authorityMap.get(endpoint.authorizedRoute);
            const serverChecked = Boolean(auth);
            const optimisticChecked = optimisticSwitches[endpoint.authorizedRoute];
            // DataGrid requires unique ids. Same route can exist for multiple HTTP methods.
            const rowId = `${endpoint.httpmethod ?? ''}::${endpoint.authorizedRoute ?? index}`;
            return {
                ...endpoint,
                id: rowId,
                roleAuthorizationId: getRoleAuthorizationId(auth),
                isAuthorized: optimisticChecked !== undefined ? optimisticChecked : serverChecked,
                isUpdating: Boolean(pendingRoutes[endpoint.authorizedRoute]),
            };
        });

        if (!searchLower) {
            return mappedRows;
        }
        return mappedRows.filter((row: any) => row.authorizedRoute?.toLowerCase?.().includes(searchLower));
    }, [
        allMenuEndPoints,
        authorityMap,
        getRoleAuthorizationId,
        optimisticSwitches,
        pendingRoutes,
        searchedAuthorityName,
    ]);

    const handleCopyRoute = useCallback((route: string) => {
        navigator.clipboard.writeText(route);
        setCopiedRoute(route);
        setTimeout(() => {
            setCopiedRoute('');
        }, 1000);
    }, []);

    const columns = useMemo(
        () => [
            { field: 'id', headerName: 'id', flex: 1, hide: true },
            {
                field: 'authorizedRoute',
                headerName: 'URL',
                minWidth: 300,
                flex: 1,
                renderCell: (params: any) => {
                    return (
                        <Box
                            className={AM.cellRoute}
                            data-authorized-route={params.row.authorizedRoute}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                width: '100%',
                                flexDirection: 'row',
                            }}
                        >
                            <Box
                                className={AM.cellRouteTooltip}
                                sx={{ display: 'flex', width: '80%', textOverflow: 'ellipsis', overflow: 'hidden' }}
                            >
                                <Tooltip title={params.row.authorizedRoute}>
                                    <Typography
                                        className={AM.cellRouteText}
                                        data-authorized-route={params.row.authorizedRoute}
                                        sx={{ fontSize: '12px !important' }}
                                    >
                                        {params.row.authorizedRoute}
                                    </Typography>
                                </Tooltip>
                            </Box>
                            <IconButton
                                className={AM.btnCopyRoute}
                                data-authorized-route={params.row.authorizedRoute}
                                data-copied={String(copiedRoute === params.row.authorizedRoute)}
                                sx={{ marginLeft: '8px' }}
                                onClick={() => handleCopyRoute(params.row.authorizedRoute)}
                            >
                                {copiedRoute === params.row.authorizedRoute ? <CheckIcon /> : <CopyAllIcon />}
                            </IconButton>
                        </Box>
                    );
                },
            },
            {
                field: 'httpmethod',
                headerName: t('authorizationManagement.apiMethod'),
                flex: 1,
                minWidth: 100,
                renderCell: (params: any) => {
                    return (
                        <Box
                            className={AM.cellMethod}
                            data-http-method={params.row.httpmethod}
                            data-authorized-route={params.row.authorizedRoute}
                            sx={{ display: 'flex', width: '100%', paddingLeft: '48px' }}
                        >
                            {params.row.httpmethod === 'POST' && (
                                <Badge
                                    className={`${AM.cellMethodBadge} ${AM.cellMethodBadgePost}`}
                                    data-http-method="POST"
                                    sx={{
                                        '& .MuiBadge-standard': {
                                            fontSize: '14px !important',
                                            fontWeight: 'bold !important',
                                            width: '100px !important',
                                            padding: '12px !important',
                                        },
                                    }}
                                    color="secondary"
                                    badgeContent="POST"
                                />
                            )}
                            {params.row.httpmethod === 'PUT' && (
                                <Badge
                                    className={`${AM.cellMethodBadge} ${AM.cellMethodBadgePut}`}
                                    data-http-method="PUT"
                                    sx={{
                                        '& .MuiBadge-standard': {
                                            fontSize: '14px !important',
                                            fontWeight: 'bold !important',
                                            width: '100px !important',
                                            padding: '12px !important',
                                        },
                                    }}
                                    color="info"
                                    badgeContent="PUT"
                                />
                            )}
                            {params.row.httpmethod === 'DELETE' && (
                                <Badge
                                    className={`${AM.cellMethodBadge} ${AM.cellMethodBadgeDelete}`}
                                    data-http-method="DELETE"
                                    sx={{
                                        '& .MuiBadge-standard': {
                                            fontSize: '14px !important',
                                            fontWeight: 'bold !important',
                                            width: '100px !important',
                                            padding: '12px !important',
                                        },
                                    }}
                                    color="error"
                                    badgeContent="DELETE"
                                />
                            )}
                            {params.row.httpmethod === 'GET' && (
                                <Badge
                                    className={`${AM.cellMethodBadge} ${AM.cellMethodBadgeGet}`}
                                    data-http-method="GET"
                                    sx={{
                                        '& .MuiBadge-standard': {
                                            fontSize: '14px !important',
                                            fontWeight: 'bold !important',
                                            width: '100px !important',
                                            padding: '12px !important',
                                        },
                                    }}
                                    color="success"
                                    badgeContent="GET"
                                />
                            )}
                            {params.row.httpmethod === 'PATCH' && (
                                <Badge
                                    className={`${AM.cellMethodBadge} ${AM.cellMethodBadgePatch}`}
                                    data-http-method="PATCH"
                                    sx={{
                                        '& .MuiBadge-standard': {
                                            fontSize: '14px !important',
                                            fontWeight: 'bold !important',
                                            width: '100px !important',
                                            padding: '12px !important',
                                        },
                                    }}
                                    color="warning"
                                    badgeContent="PATCH"
                                />
                            )}
                        </Box>
                    );
                },
            },
            {
                field: 'detay',
                headerName: t('general.active'),
                flex: 1,
                renderCell: (params: any) => {
                    return (
                        <Box
                            className={AM.cellActive}
                            data-authorized-route={params.row.authorizedRoute}
                            data-is-authorized={String(!!params.row.isAuthorized)}
                            data-is-updating={String(!!params.row.isUpdating)}
                            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                        >
                            <Switch
                                className={AM.inputCellActiveSwitch}
                                data-authorized-route={params.row.authorizedRoute}
                                data-is-authorized={String(!!params.row.isAuthorized)}
                                checked={params.row.isAuthorized}
                                disabled={params.row.isUpdating}
                                onChange={(e) => handleSwitchControl(e.target.checked, params.row)}
                            />
                            {params.row.isUpdating && <CircularProgress className={AM.cellActiveProgress} size={16} />}
                        </Box>
                    );
                },
            },
        ],
        [copiedRoute, handleCopyRoute, handleSwitchControl, t]
    );

    useEffect(() => {
        GetMenuAllEndPoints();
        GetRoleAuthorizationList();
    }, [GetMenuAllEndPoints, GetRoleAuthorizationList]);

    return (
        <SCUIContent className={AM.page}>
            <Grid container className={AM.root} data-role-id={id} data-role-name={roleName}>
                <Grid
                    className={AM.titleBox}
                    size={{
                        lg: 12,
                        md: 12,
                        xs: 12,
                    }}
                >
                    <h1 className={`headtxt ${AM.title}`}>
                        {roleName} {t('authorizationManagement.userEdit.authorizationEdit')}{' '}
                    </h1>
                </Grid>
                <Grid
                    className={AM.backBox}
                    sx={{ marginTop: '16px', marginBottom: '16px' }}
                    size={{
                        lg: 12,
                        md: 12,
                        xs: 12,
                    }}
                >
                    <SCUIButton
                        className={AM.btnBack}
                        text={t('general.back')}
                        type="primary"
                        sizeType="fixed"
                        buttonType="button"
                        size={'m'}
                        showStartIcon
                        startIcon={<ArrowBackIosNewIcon sx={{ fontSize: '16px', color: '#fff' }} />}
                        onClick={() => props.history.push(PageUrl.authorizationRuleManagement.url)}
                    />
                </Grid>

                <Paper className={AM.searchPaper} sx={{ display: 'inline-block', margin: '2rem', width: '100%' }}>
                    <TextField
                        className={AM.inputSearch}
                        value={searchedAuthorityName}
                        onChange={(searchVal) => setSearchedAuthorityName(searchVal.target.value)}
                        fullWidth
                        placeholder={'URL'}
                    />
                </Paper>
                <Paper className={AM.gridPaper} sx={{ padding: '2rem', margin: '2rem', width: '100%' }}>
                    <Box className={AM.grid}>
                        <SCUICustomDataGrid
                            pageSize={50}
                            rows={rows}
                            columns={columns}
                            initialState={{
                                sorting: {
                                    sortModel: [
                                        {
                                            field: 'authorizedRoute',
                                        },
                                    ],
                                },
                            }}
                        />
                    </Box>
                </Paper>
            </Grid>
        </SCUIContent>
    );
};
